import jwt_decode from "jwt-decode";
import { getCookie, setCookie } from "./CookieUtil";
import { trackAuthFailed } from "../../analytics/track";

export const ACCESS_TOKEN_COOKIE = "accessToken";

/**
 * auth0-spa-js only fetches a new token from `getAccessTokenSilently({ cacheMode: "on" })`
 * when the cached one is inside 60 seconds of its `exp`. Refreshing at this threshold
 * means a cacheMode "on" call will actually hit Auth0 instead of returning the cached JWT.
 */
export const REFRESH_WHEN_SECONDS_REMAINING = 60;

type GetAccessTokenSilently = (options?: {
  cacheMode?: "on" | "off" | "cache-only";
}) => Promise<string>;

/**
 * Seconds until the JWT in the cookie expires. Negative when already expired.
 * `null` when there is no cookie or the value is not a decodable JWT.
 */
export const getAccessTokenSecondsRemaining = (
  token: string | undefined
): number | null => {
  if (!token) {
    return null;
  }
  try {
    const { exp } = jwt_decode<{ exp?: number }>(token);
    if (typeof exp !== "number") {
      return null;
    }
    return exp - Date.now() / 1000;
  } catch {
    return null;
  }
};

let refreshInFlight: Promise<boolean> | null = null;

/**
 * A dead Auth0 session retries every few seconds. Report the first failure in a
 * streak, then stay quiet until a refresh succeeds, so Mixpanel is not filled
 * with one `auth_failed` per tick.
 */
let refreshFailureReported = false;

/**
 * Fetch a fresh access token from Auth0 and write it to the `accessToken` cookie.
 *
 * - Uses the SDK cache first so parallel tabs share one refresh-token exchange.
 * - Falls back to `cacheMode: "off"` if the SDK handed back the token we already hold.
 * - Concurrent callers share the same in-flight promise.
 * - Writes only when the cookie still holds the token captured at the start, so a
 *   logout or a newer token from another tab is not overwritten by this response.
 * - Never throws; failures are logged and tracked once per failure streak, and resolve `false`.
 *
 * Resolves `true` when this call replaced the cookie with a different token.
 */
export const refreshAccessTokenCookie = (
  getAccessTokenSilently: GetAccessTokenSilently,
  { force = false }: { force?: boolean } = {}
): Promise<boolean> => {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  const currentToken = getCookie(ACCESS_TOKEN_COOKIE);
  const cookieStillHoldsStartToken = () =>
    getCookie(ACCESS_TOKEN_COOKIE) === currentToken;

  refreshInFlight = (async () => {
    try {
      let token = await getAccessTokenSilently({
        cacheMode: force ? "off" : "on"
      });
      if (!cookieStillHoldsStartToken()) {
        return false;
      }
      if (!force && token === currentToken) {
        token = await getAccessTokenSilently({ cacheMode: "off" });
      }
      if (!token || !cookieStillHoldsStartToken()) {
        return false;
      }
      setCookie(ACCESS_TOKEN_COOKIE, token);
      refreshFailureReported = false;
      return token !== currentToken;
    } catch (error) {
      console.error("Access token refresh failed", error);
      if (!refreshFailureReported) {
        refreshFailureReported = true;
        trackAuthFailed("access_token_refresh", error);
      }
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
};
